import * as React from "react";
import { Props } from "../../types/global";
import { MyAccountService } from "./myaccount-service";
import { Country, Product } from "../types";
import t from "../i18n";
import { dialogService } from "react-api-dialog";
import { messageDialog } from "../dialogs/MessageDialog";
import { Loading } from "../common/Loading";
import { Link } from "react-router-dom";
import { ValidationBuilder, Validator, Form, FormRef } from "@ajayvikas/react-fluent-validation";
import { Input } from "../Controls/Input";
import { Select } from "../Controls/Select";
import { MaskedInput } from "../Controls/MaskedInput";
import { btnStyles } from "../_styles/button";
import { css } from "emotion";

import classNames from "classnames";

function contactValidator() {
    return new ValidationBuilder<ContactType>()
        .ensure("name")
        .required().withMessage(t.validations("full_name_required"))
        .ensure("mobileNumber")
        .required().withMessage(t.validations("mobile_number_required"))
        .satisfies((prop: string, obj) => !prop.startsWith("0"))
        .withMessage(t.validations("mobile_number_invalid"))
        .ensure("countryCode")
        .required().withMessage(t.validations("country_required"))
        .ensure("productId")
        .required().withMessage(t.validations("operator_required"));
}

declare type ContactType = {
    name: string,
    countryCode: string,
    mobileNumber: string,
    phonebookId: number,
    product: Product,
    productId: number
}
declare type ContactState = {
    countries: Country[],
    products: Product[],
    contact?: ContactType,
    validator: Validator<ContactType>
}
export default class Contact extends React.Component<Props<{ id: string | "new" }>> {
    readonly state: ContactState = { countries: [], products: [], validator: contactValidator().build() };
    form: FormRef | null;
    get isNew() {
        return this.props.match.params.id === "new";
    }
    get title() {
        return this.isNew ? t.myaccount("new_contact") : t.myaccount("edit_contact");
    }
    assignFormRef = (form: FormRef | null) => {
        this.form = form;
    }
    async componentDidMount() {
        document.title = this.title;
        let validator = contactValidator().build();
        let contact = {} as ContactType;
        let products: Product[] = [];
        if (!this.isNew) {
            const contactResponse = await MyAccountService.fetchPhoneBookEntry(+this.props.match.params.id)
            if (contactResponse.responseCode === 0) {
                contact = contactResponse.payLoad;
                const productResponse = await MyAccountService.getProducts(contact.countryCode);
                products = productResponse.payLoad!.products;
            }
        }
        const countryResponse = await MyAccountService.getCountries();
        const countries = countryResponse.payLoad! as Country[];
        if (countries.length === 1) {
            contact = { ...contact, countryCode: countries[0].countryCode };
            const response = await MyAccountService.getProducts(countries[0].countryCode);
            if (response.responseCode === 0) {
                products = response.payLoad!.products;
                if (products.length === 1) {
                    contact = { ...contact, productId: products[0].productId };
                    validator = this.setMobileNumberValidator(countries[0], products[0]);
                }
            }
        }
        else {
            if (contact.countryCode) {
                let country = countries.find(c => c.countryCode == contact.countryCode)!;
                validator = this.setMobileNumberValidator(country);
            }
        }
        this.setState({ countries: countryResponse.payLoad!, products: products, contact: contact, validator: validator });
    }
    handleChange = async (value: any, property: keyof ContactType) => {
        if (property === "countryCode") {
            const selectedCountry = this.state.countries.find(c => c.countryCode === value)!;
            const validator = this.setMobileNumberValidator(selectedCountry);
            const response = await MyAccountService.getProducts(value);
            this.setState({ contact: { ...this.state.contact, [property]: value }, products: response.payLoad!.products, validator: validator });
        } else {
            this.setState({ contact: { ...this.state.contact, [property]: value } });
        }
    }
    setMobileNumberValidator(selectedCountry: Country, selectedProduct?: Product) {
        const lengths = selectedCountry.localNumberLength.split(",").map(n => parseInt(n, 10));

        let validator = contactValidator()
            .ensure("mobileNumber")
            .minLength(Math.min(...lengths)).withMessage(t.validations("mobile_number_length", {
                length: lengths.join(" or ")
            }))
            .maxLength(Math.max(...lengths)).withMessage(t.validations("mobile_number_length", {
                length: lengths.join(" or ")
            }))
            .build();
        if (selectedCountry.countryCode === "HT") {
            if (selectedProduct && selectedProduct.name.toLowerCase() === "natcom") {
                let prefix: Array<string> = ["32", "33", "35", "40", "41", "42", "43"];
                validator = contactValidator()
                    .ensure("mobileNumber")
                    .minLength(Math.min(...lengths)).withMessage(t.validations("mobile_number_length", {
                        length: lengths.join(" or ")
                    }))
                    .maxLength(Math.max(...lengths)).withMessage(t.validations("mobile_number_length", {
                        length: lengths.join(" or ")
                    }))
                    .satisfies((prop: string, obj) => prefix.some(a => a == prop.substring(0, 2)))
                    .withMessage(t.validations("mobile_number_invalid"))
                    .build();
            }
        }
        return validator;
    }

    save = async () => {
        if (!this.form) return;
        const validationResult = this.form.submit();
        const isValid = validationResult?.isValid() ?? false;
        if (isValid === false) {
            messageDialog(t.error("validation_error"), this.title);
            return;
        }
        dialogService.showPreloader();
        const response = await MyAccountService.savePhoneBookEntry(this.state.contact);
        await dialogService.hidePreloader();
        if (!response) return;
        if (response.responseCode !== 0) {
            messageDialog("An error occurred while saving Contact. Please try again.", this.title);
            return;
        }
        messageDialog(t.myaccount("contact_saved"), this.title)
            .then(() => this.props.history.replace("/contact-list"));
    }
    renderCountryDropDown() {
        if (this.state.countries.length == 1) return null;
        if (!this.state.contact) return null;
        return <div className="row">
            <div className="form-group col-12">
                <h5>{t.topup("select_country_placeholder")}</h5>
                <Select className="form-control"
                    value={this.state.contact.countryCode || ""} propertyName="countryCode"
                    onChange={e => this.handleChange(e.target.value, "countryCode")}
                >
                    {!this.state.contact.countryCode && <option value="">[{t.topup("select_country")}]</option>}
                    {this.state.countries.map(c =>
                        <option key={c.countryCode} value={c.countryCode}>{c.name}</option>
                    )}
                </Select>
            </div>
        </div>
    }
    renderProductDropDown() {
        if (this.state.countries.length == 1 && this.state.products.length == 1) return null;
        if (!this.state.contact) return null;
        return <div className="row">
            <div className="form-group col-12">
                <h5>{t.topup("select_operator")}</h5>
                <Select className="form-control"
                    value={this.state.contact.productId || ""}
                    propertyName="productId"
                    onChange={e => this.handleChange(e.target.value, "productId")}
                >
                    <option value="">[{t.topup("select_operator")}]</option>
                    {this.state.products.map(p =>
                        <option key={p.productId} value={p.productId}>{p.name}</option>
                    )}
                </Select>
            </div>
        </div>
    }
    render() {
        if (!this.state.contact) return <Loading />;
        const contact = this.state.contact;
        const selectedCountryCode = this.state.contact ? this.state.contact.countryCode : undefined;
        const selectedCountry = this.state.countries.find(c => c.countryCode === selectedCountryCode);
        const localNumberLength = selectedCountry ?
            Math.max(...selectedCountry.localNumberLength.split(",").map(n => parseInt(n, 10))) : 15;
        const mask = [/[1-9]/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/].splice(0, localNumberLength);
        return (
            <div className="d-flex align-items-center" style={{ minHeight: "calc(100vh - 63px)" }}>
                <div className="auth container small-gutter">
                    <div className="auth-box">
                        <div className="form-group">
                            <h2>{this.title}</h2>
                        </div>
                        <Form ref={this.assignFormRef} object={this.state.contact} validator={this.state.validator}>
                            <div className="row">
                                <div className="form-group col-12">
                                    <h5>{t.topup("full_name_placeholder")}</h5>
                                    <Input type="text" className="form-control" maxLength={50}
                                        value={contact.name || ""} propertyName="name"
                                        onChange={e => this.handleChange(e.target.value, "name")}
                                    />
                                </div>
                            </div>
                            {this.renderCountryDropDown()}
                            {this.renderProductDropDown()}
                            <div className="row">
                                <div className="form-group col-12">
                                    <h5>{t.topup("mobile_number_placeholder")}</h5>
                                    <MaskedInput mask={mask} guide={false}
                                        className="form-control" maxLength={localNumberLength}
                                        value={contact.mobileNumber || ""} propertyName="mobileNumber"
                                        onChange={e => this.handleChange(e.target.value, "mobileNumber")}
                                    />
                                </div>
                            </div>
                            <div className="row">
                                <div className="form-group col-12 text-right">
                                    <Link to="/contact-list" className="button white">{t.common("cancel_button")}</Link>
                                    <a onClick={this.save} className={classNames(['button', css(btnStyles.default)])}>{t.common("save")}</a>
                                </div>
                            </div>
                        </Form>
                    </div>
                </div >
            </div>
        );
    }
}